import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Star, ShoppingBag, Eye, Truck, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useShop } from "../context/ShopContext";
import { inr, errMsg } from "../lib/api";

export default function ProductCard({ product }) {
  const { user } = useAuth();
  const { addToCart, toggleWishlist, wishlistIds } = useShop();
  const nav = useNavigate();
  const [quickView, setQuickView] = useState(false);
  const [adding, setAdding] = useState(false);

  const image = product.image || product.images?.[0];
  const price = product.effective_price ?? product.price;
  const flash = product.flash;
  const discount = product.discount_pct ?? (product.original_price > price ? Math.round((1 - price / product.original_price) * 100) : 0);
  const inWish = wishlistIds.includes(product.id);

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await addToCart(product, 1);
      toast.success("Added to your cart", { action: { label: "View Cart", onClick: () => nav("/cart") } });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setAdding(false);
    }
  };

  const handleWish = async (e) => {
    e.preventDefault();
    const res = await toggleWishlist(product.id);
    if (res?.needsAuth) {
      toast.info("Sign in to save gifts you love");
      nav("/auth");
      return;
    }
    toast.success(res?.added ? "Saved to your wishlist" : "Removed from wishlist");
  };

  return (
    <>
      <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5, ease: "easeOut" }} className="group relative h-full flex flex-col bg-white rounded-2xl border border-[#E9E2D7] overflow-hidden card-lift" data-testid={`product-card-${product.id}`}>
        <Link to={`/product/${product.id}`} className="block relative aspect-[4/5] overflow-hidden bg-[#F5EFE6] shrink-0">
          <img src={image} alt={product.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
          <div className="absolute top-3 left-3 flex flex-col gap-1.5">
            {flash && (
              <span className="px-2.5 py-1 rounded-full bg-[#B45309] text-white text-[10px] font-bold uppercase tracking-wider shadow-md" data-testid={`flash-badge-${product.id}`}>Flash Sale</span>
            )}
            {discount > 0 && (
              <span className="px-2.5 py-1 rounded-full bg-white/95 text-[#B45309] text-[10px] font-bold shadow-sm">{discount}% OFF</span>
            )}
          </div>
          <div className="absolute top-3 right-3 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <button onClick={handleWish} aria-label="Wishlist" className={`w-9 h-9 rounded-full grid place-items-center shadow-md transition-colors ${inWish ? "bg-[#B45309] text-white" : "bg-white text-stone-600 hover:text-[#B45309]"}`} data-testid={`wishlist-btn-${product.id}`}>
              <motion.span whileTap={{ scale: 1.5 }} transition={{ type: "spring", stiffness: 400 }}>
                <Heart size={16} fill={inWish ? "currentColor" : "none"} />
              </motion.span>
            </button>
            <button onClick={(e) => { e.preventDefault(); setQuickView(true); }} aria-label="Quick view" className="w-9 h-9 rounded-full bg-white text-stone-600 hover:text-[#B45309] grid place-items-center shadow-md transition-colors" data-testid={`quickview-btn-${product.id}`}>
              <Eye size={16} />
            </button>
          </div>
          {product.stock <= 0 && (
            <div className="absolute inset-0 bg-white/70 grid place-items-center">
              <span className="px-4 py-2 rounded-full bg-stone-900 text-white text-xs font-semibold">Currently Unavailable</span>
            </div>
          )}
        </Link>

        <div className="p-4 flex-1 flex flex-col">
          <Link to={`/product/${product.id}`}>
            <h3 className="font-semibold text-stone-800 text-sm leading-snug line-clamp-1 hover:text-[#B45309] transition-colors">{product.name}</h3>
          </Link>
          <div className="flex items-center gap-1.5 mt-1.5">
            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#78866B]/15 text-[#5c6b52] text-[11px] font-bold">
              <Star size={10} fill="currentColor" /> {product.rating || "New"}
            </span>
            {product.review_count > 0 && <span className="text-[11px] text-stone-400">({product.review_count})</span>}
          </div>
          <div className="flex items-baseline gap-2 mt-2">
            <span className="text-lg font-bold text-stone-900">{inr(price)}</span>
            {product.original_price > price && <span className="text-sm text-stone-400 line-through">{inr(product.original_price)}</span>}
          </div>
          {flash && (
            <div className="mt-2" data-testid={`stock-bar-${product.id}`}>
              <div className="h-1.5 rounded-full bg-[#F5EFE6] overflow-hidden">
                <div className="h-full rounded-full bg-[#B45309]" style={{ width: `${Math.min((flash.sold_qty / flash.max_qty) * 100, 100)}%` }} />
              </div>
              <p className="text-[11px] font-semibold text-[#B45309] mt-1">
                {flash.remaining <= 3 ? `Only ${flash.remaining} left!` : flash.remaining <= 8 ? "Selling fast!" : `${flash.sold_qty} / ${flash.max_qty} sold`}
              </p>
            </div>
          )}
          {product.delivery_types?.includes("same_day") && (
            <p className="flex items-center gap-1 text-[11px] text-[#5c6b52] font-medium mt-2"><Truck size={12} /> Same Day Delivery Available</p>
          )}
          <div className="flex-1" />
          <button onClick={handleAdd} disabled={adding || product.stock <= 0} className="mt-3 w-full h-10 rounded-full bg-[#1C1917] text-[#FDFBF7] text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[#B45309] hover:-translate-y-0.5 hover:shadow-md transition-[background-color,transform,box-shadow] duration-300 disabled:opacity-50 disabled:hover:translate-y-0" data-testid={`add-to-cart-${product.id}`}>
            <ShoppingBag size={15} /> {adding ? "Adding…" : "Add to Cart"}
          </button>
        </div>
      </motion.div>

      <AnimatePresence>
        {quickView && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] bg-stone-900/50 backdrop-blur-sm grid place-items-center p-4" onClick={() => setQuickView(false)} data-testid="quickview-modal">
            <motion.div initial={{ scale: 0.94, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, y: 16 }} className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="relative aspect-[4/3] bg-[#F5EFE6]">
                <img src={image} alt={product.name} className="w-full h-full object-cover" />
                <button onClick={() => setQuickView(false)} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white grid place-items-center shadow-md" aria-label="Close" data-testid="quickview-close"><X size={16} /></button>
              </div>
              <div className="p-6">
                <h3 className="font-serif text-xl font-semibold text-stone-900">{product.name}</h3>
                <div className="flex items-baseline gap-2 mt-2">
                  <span className="text-2xl font-bold text-stone-900">{inr(price)}</span>
                  {product.original_price > price && <span className="text-stone-400 line-through">{inr(product.original_price)}</span>}
                  {discount > 0 && <span className="text-xs font-bold text-[#B45309]">{discount}% OFF</span>}
                </div>
                <p className="text-sm text-stone-500 mt-3 line-clamp-2">{product.description}</p>
                <div className="flex gap-3 mt-5">
                  <button onClick={handleAdd} disabled={adding || product.stock <= 0} className="flex-1 h-11 rounded-full border-2 border-[#1C1917] text-stone-900 text-sm font-semibold hover:bg-[#F5EFE6] transition-colors disabled:opacity-50" data-testid="quickview-add-cart">Add to Cart</button>
                  <Link to={`/product/${product.id}`} className="flex-1 h-11 rounded-full bg-[#B45309] text-white text-sm font-semibold grid place-items-center hover:bg-[#92400E] transition-colors" data-testid="quickview-details">View Details</Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
